import { ELEMENT_EFFECTIVENESS } from "./Utils";
import { SPRITES_MAP } from "./SpritesMap";

const IO_GREEN_PORTRAIT =
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/profile_images%2Fio%20green.png?alt=media&token=3e5bcb08-2e5e-4b30-84e0-9ec124b8c49e";
const IO_BLUE_PORTRAIT =
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/profile_images%2Fio%20blue.png?alt=media&token=d4bd30e7-49d6-4f7b-bd87-415c77a80174";
const IO_RED_PORTRAIT =
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/profile_images%2Fio%20red.png?alt=media&token=0b2556be-af99-4abc-9a28-a821c0225aff";

const STORY_COMPANIONS = Object.freeze([
  Object.freeze({
    id: "io-green",
    name: "Green I.O.",
    color: "Green",
    element: "grass",
    avatarKey: "io_default",
    image: SPRITES_MAP.io_default,
    choiceImage:
      "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-green.png?alt=media&token=feff4088-f1da-43f7-9c7c-c231fe45b240",
    portrait: IO_GREEN_PORTRAIT,
    accent: "#3bd979",
    secondaryAccent: "#b9ffcb",
  }),
  Object.freeze({
    id: "io-blue",
    name: "Blue I.O.",
    color: "Blue",
    element: "water",
    avatarKey: "i_o_blue",
    image: SPRITES_MAP.i_o_blue,
    choiceImage:
      "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-blue.png?alt=media&token=5cdc5b7b-5160-4869-bce2-b6c9962d03f5",
    portrait: IO_BLUE_PORTRAIT,
    accent: "#3ea6ff",
    secondaryAccent: "#b7e0ff",
  }),
  Object.freeze({
    id: "io-red",
    name: "Red I.O.",
    color: "Red",
    element: "fire",
    avatarKey: "i_o_red",
    image: SPRITES_MAP.i_o_red,
    choiceImage:
      "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-red.png?alt=media&token=9adfe9df-a34b-4630-ad9e-614e85e74da7",
    portrait: IO_RED_PORTRAIT,
    accent: "#ff675c",
    secondaryAccent: "#ffd0ca",
  }),
]);

export const STORY_COMPANION_UNLOCK_ROSTER = Object.freeze(
  STORY_COMPANIONS.map((companion) => companion.id),
);

const STORY_COMPANION_BY_ID = new Map(
  STORY_COMPANIONS.map((companion) => [companion.id, companion]),
);

export const normalizeStoryCompanionId = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const resolveStoryCompanionById = (companionId = "") =>
  STORY_COMPANION_BY_ID.get(normalizeStoryCompanionId(companionId)) || null;

export const normalizeStoryCompanionRoster = (roster = []) => {
  const normalizedRoster = Array.isArray(roster)
    ? roster.map((entry) => normalizeStoryCompanionId(entry)).filter(Boolean)
    : [];

  return [...new Set(normalizedRoster)].filter((companionId) =>
    STORY_COMPANION_BY_ID.has(companionId),
  );
};

export const buildStoryCompanionMatchupMap = (element = "") => {
  const normalizedElement = String(element || "").trim().toLowerCase();
  const effectiveness = ELEMENT_EFFECTIVENESS?.[normalizedElement] || {};

  return Object.entries(effectiveness).reduce(
    (summary, [targetElement, value]) => {
      if (Number(value) > 0) {
        summary.strongAgainst.push(targetElement);
      } else if (Number(value) < 0) {
        summary.weakAgainst.push(targetElement);
      }

      return summary;
    },
    {
      strongAgainst: [],
      weakAgainst: [],
    },
  );
};

export const getStoryCompanionPreview = (companionId = "") => {
  const companion = resolveStoryCompanionById(companionId);
  if (!companion) return null;

  return {
    ...companion,
    matchup: buildStoryCompanionMatchupMap(companion.element),
  };
};

export const resolveActiveStoryCompanion = (storyState = null) => {
  if (!storyState?.storyCompanionUnlocked) return null;

  const roster = normalizeStoryCompanionRoster(storyState?.storyCompanionRoster);
  const activeId = normalizeStoryCompanionId(storyState?.activeStoryCompanionId);
  const resolvedId = roster.includes(activeId) ? activeId : null;

  return resolvedId ? getStoryCompanionPreview(resolvedId) : null;
};

const FAVORABLE_AURA = Object.freeze({
  verdict: "favorable",
  attackDelta: 1,
  accuracyDelta: 1,
});

const UNFAVORABLE_AURA = Object.freeze({
  verdict: "unfavorable",
  attackDelta: -1,
  accuracyDelta: -1,
});

const NEUTRAL_AURA = Object.freeze({
  verdict: "neutral",
  attackDelta: 0,
  accuracyDelta: 0,
});

// Companion I.O. auras stay intentionally simple for the first pass:
// compare the equipped element to the enemy lineup and only apply a bonus or
// penalty when the full matchup leans one way.
export const resolveStoryCompanionAura = ({
  activeStoryCompanionId = "",
  enemyElements = [],
} = {}) => {
  const companion = resolveStoryCompanionById(activeStoryCompanionId);
  if (!companion) {
    return {
      companion: null,
      verdict: NEUTRAL_AURA.verdict,
      attackDelta: 0,
      accuracyDelta: 0,
    };
  }

  let hasAdvantage = false;
  let hasDisadvantage = false;

  enemyElements.forEach((enemyElement) => {
    const bonus =
      Number(
        ELEMENT_EFFECTIVENESS?.[companion.element]?.[
          String(enemyElement || "").trim().toLowerCase()
        ] || 0,
      ) || 0;

    if (bonus > 0) hasAdvantage = true;
    if (bonus < 0) hasDisadvantage = true;
  });

  const aura =
    hasAdvantage && !hasDisadvantage
      ? FAVORABLE_AURA
      : hasDisadvantage && !hasAdvantage
        ? UNFAVORABLE_AURA
        : NEUTRAL_AURA;

  return {
    companion: getStoryCompanionPreview(companion.id),
    ...aura,
  };
};

export const STORY_COMPANIONS_REGISTRY = STORY_COMPANIONS;
